import {
  Avatar,
  Box,
  MenuItem,
  Button,
  Link,
  ButtonGroup,
  Divider,
  FormLabel,
  Container,
  Grid,
  styled,
  Typography,
  FormControlLabel,
  Popover,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  Hidden,
  IconButton,
  Checkbox,
  Tooltip,
  useMediaQuery,
  useTheme,
  Select,
} from "@mui/material";
import {
  KeyboardArrowDownRounded,
  Email,
  Person,
  Lock,
  Facebook,
  Call,
  FiberManualRecord,
  Menu,
  Close,
  Verified,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import LOGO from "../assets/logo_2.png";
import LOGOmb from "../assets/text_logo.png";
import EMAIL from "../assets/email_verify.png";
import Google from "../assets/google.svg";
import {
  loginReducer,
  signupReducer,
  loginState,
  signupState,
  signupRoleOptions,
} from "../reducers";
import { US_STATES } from "../constants";
import { routeAfterSignup } from "../constants/functions";
import { LoadingButton } from "@mui/lab";
import { useDispatch, connect, useSelector } from "react-redux";
import {
  userSignUp,
  userLogin,
  signoutUser,
  getUserData,
  googleOAuth,
  facebookOAuth,
  googleOneTap,
  fetchCategories,
} from "../actions";
import SideBar from "./SideBar";
import base from "../api/apis";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import usePopover from "../hooks/usePopover";
import { AnimatePresence, motion } from "framer-motion";

const WrapBox = styled(Box)(({ theme }) => ({
  padding: "0.57rem 0",
  borderBottom: `1px solid ${grey[300]}`,
}));
const DownArrow = styled(KeyboardArrowDownRounded)(({ theme }) => ({
  fontSize: "24px",
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: ".5rem",
  marginBottom: "1.5rem",
}));
const NavLink = styled(Link)(({ theme }) => ({
  transition: "all .2s ease",
  "& p": {
    fontWeight: 400,
  },
  "&:hover": {
    color: theme.palette.tertiary.main,
  },
}));
const useStyles = makeStyles((theme) => ({
  field: {
    marginTop: ".5rem",
    marginBottom: "1rem",
  },
  logo: {
    width: "60%",
    display: "block",
    margin: "0 auto",
    marginBottom: "1rem",
  },
  label: {
    fontSize: "1rem",
  },
  flex: {
    marginBottom: "2rem",
    display: "flex",
    gap: "1rem",
    justifyContent: "center",
    alignItems: "center",
  },
  btnGoogle: {
    padding: ".5rem 0",
    borderRadius: "8px",
    textTransform: "none",
    marginBottom: "1rem",
  },
  hoverActive: {
    borderBottom: `3px solid ${theme.secondaryColor}`,
  },
}));

const NavBar = (props) => {
  const { getPopoverProps, closePopover } = usePopover();
  const {
    auth,
    currentUser,
    userSignUp,
    userLogin,
    signoutUser,
    googleOAuth,
    googleOneTap,
    facebookOAuth,
  } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const smBP = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const mdBP = useMediaQuery(theme.breakpoints.up("md"));

  const createCategories = useSelector((state) => state.createCategories);
  const { isLogin } = useSelector((state) => state.authType);

  const [anchorElProfile, setAnchorElProfile] = React.useState(null);
  const [loading, setLoading] = useState(false);
  const [openDraw, setOpenDraw] = useState(false);
  const [forgetPass, setForgetPass] = useState(false);
  const [canNotSubmit, setCanNotSubmit] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [page, setPage] = useState(1);
  const ref = useRef();
  const googleLink = useLocation().search.split("&")[0];
  const facebookLink = useLocation().search.split("&")[2];
  const path = useLocation().search;
  const location = useLocation().pathname;

  const showSearch = useMemo(() => {
    return location.includes("/listing/") && mdBP;
  }, [location, mdBP]);

  useEffect(() => {
    dispatch({ type: "AUTH_TYPE", payload: true });
  }, [dispatch]);

  // Google Auth OneTap init
  useEffect(() => {
    const handleGoogleOneTap = () => {
      window?.google?.accounts?.id.initialize({
        client_id:
          "61807210049-7akpgsb23ro9r1a495121mt2f3t3pdsb.apps.googleusercontent.com",
        callback: (response) => googleOneTap(response?.credential),
      });
      window?.google?.accounts?.id.prompt();
    };

    if (currentUser) {
      return;
    } else {
      handleGoogleOneTap();
    }
    // eslint-disable-next-line
  }, [googleOneTap]);

  useEffect(() => {
    if (path) {
      if (googleLink?.startsWith("?code")) {
        //console.log('signing with Google')
        let googleAuthCode = path.split("&")[0].split("=")[1];
        googleAuthCode = _.replace(googleAuthCode, "%2F", "/");
        googleOAuth(googleAuthCode, location);
      }
    }
    if (path) {
      if (facebookLink?.startsWith("code")) {
        //console.log('signing with Facebook')
        const facebookAuthCode = path.split("&")[2].split("=")[1];
        facebookOAuth(facebookAuthCode, location);
      }
    }
  }, [path, location, googleLink, facebookLink, googleOAuth, facebookOAuth]);

  // Popover states
  const openProfile = Boolean(anchorElProfile);

  // Sidebar Handler
  const toggleDrawer = () => {
    setOpenDraw(!openDraw);
  };

  // Auth Modal Reducers & States
  const [signupDetails, signupDispatch] = useReducer(
    signupReducer,
    signupState,
  );
  const [loginDetails, loginDispatch] = useReducer(loginReducer, loginState);

  // Popover Function Calls
  const handleClick = (event) => {
    setAnchorElProfile(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElProfile(null);
  };

  useEffect(() => {
    if (
      signupDetails.firstname === "" ||
      signupDetails.lastname === "" ||
      signupDetails.phone === "" ||
      signupDetails.email === "" ||
      signupDetails.password === "" ||
      signupDetails.confirmPassword === "" ||
      signupDetails.state === "" ||
      signupDetails.role === ""
    ) {
      setCanNotSubmit(true);
    } else {
      setCanNotSubmit(false);
    }
  }, [
    signupDetails.firstname,
    signupDetails.lastname,
    signupDetails.phone,
    signupDetails.email,
    signupDetails.password,
    signupDetails.confirmPassword,
    signupDetails.state,
    signupDetails.role,
  ]);

  // Auth - Login / Signup
  const onSubmit = async (type) => {
    if (type === "login") {
      // console.log(loginDetails)

      //do validation checks
      if (loginDetails.email === "" || loginDetails.password === "") {
        dispatch({ type: "ERROR", payload: "Invalid Credentials" });
        return;
      }
      if (!loginDetails.email.includes("@")) {
        dispatch({ type: "ERROR", payload: "Invalid email address" });
        return;
      }
      if (loginDetails.password.length < 8) {
        dispatch({
          type: "ERROR",
          payload: "Password must be more than 8 characters ",
        });
        return;
      }
      const data = {
        email: loginDetails.email,
        password: loginDetails.password,
      };
      setLoading(true); //loader
      const status = await userLogin(data);
      if (status === 200) {
        setLoading(false);
        dispatch({ type: "AUTH_CLOSE" }); // close auth modal
        loginDispatch({ type: "RESET" }); //reset loginState
      } else {
        setLoading(false);
      }
    }
    if (type === "signup") {
      //do validation checks
      if (
        signupDetails.firstname === "" ||
        signupDetails.lastname === "" ||
        signupDetails.phone === "" ||
        signupDetails.email === "" ||
        signupDetails.password === "" ||
        signupDetails.confirmPassword === "" ||
        signupDetails.state === "" ||
        signupDetails.role === ""
      ) {
        return;
      }
      //do password validation checks
      if (signupDetails.password !== signupDetails.confirmPassword) {
        dispatch({ type: "ERROR", payload: "Passwords do not match" });
        return;
      }
      const data = {
        category: "real_estate_agent",
        realty_agent: signupDetails.isAgent,
        broker: signupDetails.isBroker,
        is_internship: signupDetails.isInternship,
        role: signupDetails.role,
        phone: signupDetails.phone,
        state: signupDetails.state,
        zip_code: signupDetails.zip || "12345",
        user: {
          email: signupDetails.email,
          password: signupDetails.password,
          first_name: signupDetails.firstname,
          last_name: signupDetails.lastname,
        },
      };
      //console.log(data)
      setLoading(true); //loader
      const status = await userSignUp(data, signupDetails.isAgent);
      if (status === 200) {
        setLoading(false);
        signupDispatch({ type: "RESET" }); //reset signupState
        // Keep the modal open so the user can verify their email; role-based
        // rerouting happens after verification in verifyAccount().
        dispatch({ type: "AUTH_LOGIN" });
      } else {
        setLoading(false);
      }
    }
  };

  // Auth Signout
  const signOut = () => {
    signoutUser();
  };

  // Oauth with Google & Facebook
  const googleAuth = () => {
    window.location.assign(
      `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${window.location.origin}/&prompt=consent&response_type=code&client_id=61807210049-7akpgsb23ro9r1a495121mt2f3t3pdsb.apps.googleusercontent.com&scope=openid%20email%20profile&access_type=offline`,
    );
  };

  const facebookAuth = async () => {
    window.location.assign(
      `https://www.facebook.com/v15.0/dialog/oauth?client_id=478122010963405&redirect_uri=${window.location.origin}/&state=login&return_scopes=true&scope=email`,
    );
  };

  // Password Reset
  const onPassReset = async () => {
    if (resetEmail === "" || !resetEmail.includes("@")) return;
    setLoading(true);
    // connect to endpoint
    await base
      .post("/core/reset-password/", {
        email: resetEmail,
        url: window.location.origin,
      })
      .then((res) => {
        //console.log(res)
        setLoading(false);
        setPage(2);
        dispatch({ type: "SUCCESS", payload: "Password reset link sent" });
      })
      .catch((err) => {
        //console.log(err)
        setLoading(false);
        dispatch({ type: "ERROR", payload: err.response.data.data });
      });
  };

  const accessDashboard = (link) => {
    if (currentUser && currentUser?.is_email_verified) {
      const accessToken = localStorage.getItem("uta");
      const refreshToken = localStorage.getItem("utr");
      window.location.assign(
        `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}?uta=${accessToken}&utr=${refreshToken}&link=${link}`,
      );
    } else {
      handleClose();
      dispatch({ type: "AUTH_LOGIN" });
    }
  };

  // Auto focus Email token
  const autoFocusHandler = (event) => {
    const element = event.target;
    const nextSibling = element.nextElementSibling;
    nextSibling ? nextSibling.focus() : element.blur();
  };

  // fetch all categories
  useEffect(() => {
    if (!currentUser) return;
    dispatch(fetchCategories());
  }, [dispatch, createCategories, currentUser]);

  // Verify Email Refs
  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();
  const ref5 = useRef();
  const ref6 = useRef();

  const sendOTPCode = async () => {
    try {
      // dispatch({
      //   type: "SUCCESS",
      //   payload: "Sending new code. Please wait...",
      // });
      const { data: res } = await base.post("core/resend-otp/", {
        user_id: currentUser?.id,
      });
      if (res?.status === "success") {
        dispatch({
          type: "SUCCESS",
          payload: "New activation code has been sent to your email",
        });
      }
    } catch (error) {
      dispatch({ type: "ERROR", payload: "Sorry, could not send code" });
      return;
    }
  };

  const verifyAccount = async () => {
    const code =
      ref1.current?.value +
      ref2.current?.value +
      ref3.current?.value +
      ref4.current?.value +
      ref5.current?.value +
      ref6.current?.value;
    if (code === "") {
      dispatch({
        type: "ERROR",
        payload: "Invalid. Please provide activation code",
      });
      return;
    }
    if (code?.length < 6) {
      dispatch({
        type: "ERROR",
        payload: "Invalid activation code. Please check your code",
      });
      return;
    }
    try {
      setLoading(true);
      const { data: res } = await base.post("core/verify-email/", {
        user_id: currentUser?.id,
        otp: +code,
      });
      if (res?.status === "success") {
        const signedUpRole = currentUser?.role;
        const user = await getUserData();
        // getUserData() only resolves for realty_agent/client; for other roles
        // keep the signed-up user so the payment pages still see currentUser.
        dispatch({
          type: "LOGIN_USER",
          payload: user || { ...currentUser, is_email_verified: true },
        });
        dispatch({ type: "AUTH_CLOSE" });
        dispatch({ type: "SUCCESS", payload: res?.message });
        routeAfterSignup(signedUpRole, navigate);
      }
    } catch (error) {
      if (error?.response?.data) {
        dispatch({ type: "ERROR", payload: error?.response?.data?.detail });
      } else {
        dispatch({
          type: "ERROR",
          payload: "Sorry, email verification failed. Please try again",
        });
      }
      return;
    } finally {
      setLoading(false);
    }
  };

  const headerLinks = [
    {
      key: "buy",
      name: "Buy",
      route: "/homes-for-sale",
      subMenu: [
        {
          title: "Home For Sale",
          list: [
            {
              name: "Home Search",
              route: "/homes-for-sale",
            },
            {
              name: "Open Houses",
              route: "/open-houses",
            },
            {
              name: "Sold Homes",
              route: "/sold-homes",
            },
          ],
        },
        {
          title: "BPO Buying Options",
          list: [
            {
              name: "Buy With Partner Agent",
              route: "/partners",
            },
            {
              name: "Find A Buyer Agent",
              route: "/agents",
            },
          ],
        },
        {
          title: "Resources",
          list: [
            {
              name: "Buyer's Checklist",
              route: "/blog/buyer-checklist",
            },
            {
              name: "First Time Buyers",
              route: "/blog/buyer-firsttimer",
            },
          ],
        },
        {
          title: "Join BPO Homes",
          list: [
            {
              name: "Interested in Joining the team?",
              route: "/join",
            },
          ],
        },
      ],
    },
    {
      name: "Rent",
      route: "/homes-for-rent",
    },
    {
      key: "sell",
      name: "Sell",
      route: "/sell",
      subMenu: [
        {
          title: "My Home",
          list: [
            {
              name: "A BPO Home Value",
              route: "/blog/sellers-guide",
            },
            {
              name: "My BPO Dashboard",
              action: (e) => {
                e.stopPropagation();
                if (currentUser) {
                  accessDashboard("/profile");
                } else {
                  dispatch({ type: "AUTH_LOGIN" });
                }
              },
            },
          ],
        },
        {
          title: "BPO Selling Options",
          list: [
            {
              name: "Find A Listing Agent",
              route: "/agents",
            },
          ],
        },
        {
          title: "Resources",
          list: [
            {
              name: "Seller's Guide",
              route: "/blog/sellers-guide",
            },
            {
              name: "Home Improvement Trends",
              route: "/blog/home-improvement",
            },
            {
              name: "Explore My Options",
              route: "/blog/explore",
            },
          ],
        },
        {
          title: "Join BPO Homes",
          list: [
            {
              name: "Interested in Joining the team?",
              route: "/join",
            },
            {
              name: "BPO Agent Offices",
              route: "/realty-agents",
            },
          ],
        },
      ],
    },
    {
      key: "agent",
      name: "Agent Finder",
      route: "/agents",
      subMenu: [
        {
          title: "BPO Agents",
          list: [
            {
              name: "Find an Agent",
              route: "/agents",
            },
            {
              name: "Join as an Agent",
              route: "/join-as-agent",
            },
            {
              name: "BPO Realty Agents",
              route: "/realty-agents",
            },
          ],
        },
      ],
    },
    {
      name: "Industry Professionals",
      route: `https://${
        process.env.REACT_APP_URL.includes("dev") ? "testing." : ""
      }industryprofessionals.com`,
      target: "_blank",
    },
    {
      key: "join",
      name: "Join Our Team",
      route: "/join-as-agent",
      subMenu: [
        {
          title: "Brokerage",
          list: [
            {
              name: "Join as an Agent",
              route: "/join-as-agent",
            },
            {
              name: "Join as a Wholesaler",
              route: "/wholesale",
            },
            {
              name: "Join our Agent Internship Program",
              route: "/brokerage",
            },
            {
              name: "Get MLO License",
              route: "/get-mlo-license",
            },
            {
              name: "Get Life Insurance License",
              route: "/get-life-insurance-license",
            },
          ],
        },
      ],
    },
    {
      name: "Order BPO Report",
      route: `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}order-bpo-report`,
    },
  ];

  useEffect(() => {
    if (auth && currentUser && !currentUser?.is_email_verified) sendOTPCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, currentUser]);

  return (
    <>
      <WrapBox ref={ref} id="scrollRef">
        <Container maxWidth="xl" sx={{ pr: { xs: 1, md: 3 } }}>
          <Stack
            width="100%"
            direction="row"
            gap={{ xs: 1.5, md: 1 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <img
                src={mdBP ? LOGO : LOGOmb}
                style={{
                  width: mdBP ? "219.625px" : smBP ? "70%" : "60%",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
                alt="logo"
              />
            </Stack>

            <Hidden lgDown>
              <Stack direction="row" gap={2.5} alignItems="center">
                {headerLinks.map((nav) => {
                  const popoverProps = nav.key ? getPopoverProps(nav.key) : {};

                  return (
                    <NavLink
                      key={nav.key ?? nav.name}
                      underline="hover"
                      onMouseEnter={
                        nav.subMenu ? popoverProps.onMouseEnter : undefined
                      }
                      onMouseLeave={
                        nav.subMenu ? popoverProps.onMouseLeave : undefined
                      }
                      href={nav.route}
                      target={nav.target}
                      color="black"
                    >
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        {nav.name} {nav.subMenu && <DownArrow />}
                      </Typography>

                      {nav.subMenu && (
                        <Popover
                          disableScrollLock
                          disableAutoFocus
                          disableEnforceFocus
                          open={popoverProps.open}
                          anchorEl={popoverProps.anchorEl}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                          sx={{
                            top: "3%",
                            pointerEvents: "none",
                            "& .MuiPaper-root": {
                              top: "2.5rem !important",
                            },
                          }}
                          PaperProps={{
                            style: {
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              borderRadius: 0,
                            },
                          }}
                          onClose={() => closePopover(nav.key)}
                        >
                          <AnimatePresence>
                            {popoverProps.open && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                style={{ pointerEvents: "auto" }}
                              >
                                <Card
                                  elevation={0}
                                  sx={{
                                    pointerEvents: "auto",
                                    boxShadow:
                                      "0 4px 16px 0 rgba(19, 19, 19, .12), 0 1px 4px 0 rgba(19, 19, 19, .08)",
                                    borderRadius: 4,
                                    mt: "6px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <CardContent
                                    sx={{
                                      width:
                                        nav.subMenu.length >= 3
                                          ? "55rem"
                                          : "12.5rem",
                                      padding: "1.75rem 2.5rem",
                                    }}
                                  >
                                    <Grid container spacing={1}>
                                      {nav.subMenu.map((sub, idx) => (
                                        <Grid
                                          key={idx}
                                          item
                                          sm={nav.subMenu.length >= 3 ? 3 : 12}
                                        >
                                          <Typography
                                            variant="body"
                                            component="h6"
                                            color="text.main"
                                            mb={2.5}
                                            fontWeight={500}
                                          >
                                            {sub.title}
                                          </Typography>
                                          <Stack gap={2}>
                                            {sub.list.map((subList, _idx) => (
                                              <NavLink
                                                key={_idx}
                                                underline="hover"
                                                href={subList.route}
                                                onClick={(e) => {
                                                  if (subList.action) {
                                                    e.preventDefault();
                                                    subList.action(e);
                                                  }
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    cursor: "pointer",
                                                    fontSize: "14px",
                                                    color: "neutral.dark",
                                                  }}
                                                >
                                                  {subList.name}
                                                </Typography>
                                              </NavLink>
                                            ))}
                                          </Stack>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Popover>
                      )}
                    </NavLink>
                  );
                })}
              </Stack>
            </Hidden>

            <Stack direction={"row"} gap={0.5} alignItems="center">
              {currentUser && currentUser.role === "realty_agent" ? (
                <Box
                  display={"flex"}
                  gap=".4rem"
                  ml={showSearch ? "0rem" : "1rem"}
                  alignItems="center"
                >
                  <Avatar
                    onClick={(e) => handleClick(e)}
                    sx={{
                      ml: 1,
                      height: "2.8rem",
                      width: "2.8rem",
                      bgcolor: "primary.main",
                      cursor: "pointer",
                    }}
                  >
                    {currentUser.first_name?.charAt(0).toUpperCase()}
                  </Avatar>

                  <Popover
                    sx={{ mt: 1 }}
                    open={openProfile}
                    anchorEl={anchorElProfile}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <Box width="19rem">
                      <Box padding={"1.5rem"} textAlign="center">
                        <Box
                          position={"relative"}
                          width="fit-content"
                          m={"0 auto"}
                        >
                          <Avatar
                            src={
                              currentUser?.profile_image
                                ? currentUser?.profile_image
                                : null
                            }
                            sx={{
                              ml: 1,
                              height: "4rem",
                              width: "4rem",
                              m: "0 auto",
                              bgcolor: "secondary.main",
                            }}
                          >
                            {currentUser?.profile_image ? null : (
                              <Typography variant="h4">
                                {currentUser?.first_name
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </Typography>
                            )}
                          </Avatar>
                          {currentUser && currentUser?.is_email_verified && (
                            <Tooltip title="Verified Account" arrow>
                              <Verified
                                color="primary"
                                sx={{
                                  fontSize: "1.8rem",
                                  position: "absolute",
                                  bottom: "-7px",
                                  right: "-7px",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="h6" mt={2}>
                          {currentUser?.first_name +
                            " " +
                            currentUser?.last_name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mb={2}
                        >
                          {currentUser?.email}
                        </Typography>
                        <Button
                          sx={{
                            border: `1px solid ${grey[300]}`,
                            borderRadius: "5px",
                            textTransform: "none",
                            width: "75%",
                          }}
                          color="secondary"
                          onClick={() => accessDashboard("/agent-dashboard")}
                        >
                          Manage your account
                        </Button>
                      </Box>
                      <Divider sx={{ bgcolor: grey[100] }} />
                      <Box padding={"1rem"}>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/agent-dashboard")}
                        >
                          Dashboard
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/profile")}
                        >
                          My Profile
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/leads-notification")}
                        >
                          Leads Notification
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/my-subscriptions")}
                        >
                          My Subscriptions
                        </MenuItem>
                      </Box>
                      <Divider sx={{ bgcolor: grey[100] }} />
                      <Box padding={"1.5rem 2rem"} textAlign={"center"}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          disableElevation
                          sx={{
                            textTransform: "none",
                            background: "transparent",

                            "&:hover": {
                              color: "#fff",
                              background: (theme) =>
                                theme.palette.secondary.main,
                            },
                          }}
                          onClick={signOut}
                        >
                          Sign Out
                        </Button>
                        <span
                          style={{
                            display: "flex",
                            marginTop: "1rem",
                            justifyContent: "space-evenly",
                            alignItems: "center",
                          }}
                        >
                          <Typography>
                            <Link
                              sx={{
                                fontSize: ".9rem",
                                color: grey[500],
                                cursor: "pointer",
                                "&:hover": {
                                  color: (theme) => theme.primaryColor,
                                },
                              }}
                              underline="none"
                              href="#"
                            >
                              Terms of Services
                            </Link>
                          </Typography>
                          <FiberManualRecord sx={{ fontSize: ".4rem" }} />
                          <Typography>
                            <Link
                              sx={{
                                fontSize: ".9rem",
                                color: grey[500],
                                cursor: "pointer",
                                "&:hover": {
                                  color: (theme) => theme.primaryColor,
                                },
                              }}
                              underline="none"
                              href="/privacy"
                            >
                              Privacy Policy
                            </Link>
                          </Typography>
                        </span>
                      </Box>
                    </Box>
                  </Popover>
                </Box>
              ) : currentUser && currentUser.role === "client" ? (
                <Box
                  display={"flex"}
                  gap=".4rem"
                  ml={showSearch ? "0rem" : "1rem"}
                  alignItems="center"
                >
                  <Avatar
                    onClick={(e) => handleClick(e)}
                    sx={{
                      ml: 1,
                      height: "2.8rem",
                      width: "2.8rem",
                      bgcolor: "primary.main",
                      cursor: "pointer",
                    }}
                  >
                    {currentUser.first_name?.charAt(0).toUpperCase()}
                  </Avatar>

                  <Popover
                    sx={{ mt: 1 }}
                    open={openProfile}
                    anchorEl={anchorElProfile}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <Box width="19rem">
                      <Box padding={"1.5rem"} textAlign="center">
                        <Box
                          position={"relative"}
                          width="fit-content"
                          m={"0 auto"}
                        >
                          <Avatar
                            src={
                              currentUser?.profile_image
                                ? currentUser?.profile_image
                                : null
                            }
                            sx={{
                              ml: 1,
                              height: "4rem",
                              width: "4rem",
                              m: "0 auto",
                              bgcolor: "secondary.main",
                            }}
                          >
                            {currentUser?.profile_image ? null : (
                              <Typography variant="h4">
                                {currentUser?.first_name
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </Typography>
                            )}
                          </Avatar>
                          {currentUser && currentUser?.is_email_verified && (
                            <Tooltip title="Verified Account" arrow>
                              <Verified
                                color="primary"
                                sx={{
                                  fontSize: "1.8rem",
                                  position: "absolute",
                                  bottom: "-7px",
                                  right: "-7px",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="h6" mt={2}>
                          {currentUser?.first_name +
                            " " +
                            currentUser?.last_name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mb={2}
                        >
                          {currentUser?.email}
                        </Typography>
                        <Button
                          sx={{
                            border: `1px solid ${grey[300]}`,
                            borderRadius: "5px",
                            textTransform: "none",
                            width: "75%",
                          }}
                          color="secondary"
                          onClick={() => accessDashboard("/profile")}
                        >
                          Manage your account
                        </Button>
                      </Box>
                      <Divider sx={{ bgcolor: grey[100] }} />
                      <Box padding={"1rem"}>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/client-dashboard")}
                        >
                          Dashboard
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/profile")}
                        >
                          My Profile
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                          onClick={() => accessDashboard("/saved-homes")}
                        >
                          My Saved Homes
                        </MenuItem>
                        <MenuItem
                          component="a"
                          sx={{ borderRadius: "8px", fontSize: "1.1rem" }}
                        >
                          My Team
                        </MenuItem>
                      </Box>
                      <Divider sx={{ bgcolor: grey[100] }} />
                      <Box padding={"1.5rem 2rem"} textAlign={"center"}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          disableElevation
                          sx={{
                            textTransform: "none",
                            background: "transparent",

                            "&:hover": {
                              color: "#fff",
                              background: (theme) =>
                                theme.palette.secondary.main,
                            },
                          }}
                          onClick={signOut}
                        >
                          Sign Out
                        </Button>
                        <span
                          style={{
                            display: "flex",
                            marginTop: "1rem",
                            justifyContent: "space-evenly",
                            alignItems: "center",
                          }}
                        >
                          <Typography>
                            <Link
                              sx={{
                                fontSize: ".9rem",
                                color: grey[500],
                                cursor: "pointer",
                                "&:hover": {
                                  color: (theme) => theme.primaryColor,
                                },
                              }}
                              underline="none"
                              href="#"
                            >
                              Terms of Services
                            </Link>
                          </Typography>
                          <FiberManualRecord sx={{ fontSize: ".4rem" }} />
                          <Typography>
                            <Link
                              sx={{
                                fontSize: ".9rem",
                                color: grey[500],
                                cursor: "pointer",
                                "&:hover": {
                                  color: (theme) => theme.primaryColor,
                                },
                              }}
                              underline="none"
                              href="#"
                            >
                              Privacy Policy
                            </Link>
                          </Typography>
                        </span>
                      </Box>
                    </Box>
                  </Popover>
                </Box>
              ) : (
                <Button
                  variant={mdBP ? "contained" : "outlined"}
                  size="small"
                  disableElevation
                  color={mdBP ? "primary" : "inherit"}
                  sx={{
                    borderRadius: "50px",
                    width: { xs: "3rem", md: "8rem" },
                    lineHeight: 1,
                    height: { xs: "1.5rem", md: "2.5rem" },
                    fontWeight: { xs: "700", md: "400" },
                    fontSize: "14px",
                    borderWidth: 0,
                    bgcolor: { xs: "background.default", md: "primary.main" },
                  }}
                  onClick={() => {
                    dispatch({ type: "AUTH_LOGIN" });
                  }}
                >
                  {mdBP ? "Register/Login" : "Signin"}
                </Button>
              )}

              <Hidden lgUp>
                <IconButton onClick={toggleDrawer} sx={{ cursor: "pointer" }}>
                  <Menu color="text" />
                </IconButton>
              </Hidden>
            </Stack>
          </Stack>
        </Container>
      </WrapBox>

      {/* Auth Modal */}
      <Dialog
        open={auth ? true : false}
        fullWidth
        maxWidth={"xs"}
        onClose={() => {
          loginDispatch({ type: "RESET" });
          signupDispatch({ type: "RESET" });
          dispatch({ type: "AUTH_CLOSE" });
        }}
      >
        <DialogContent
          sx={{
            "-ms-overflow-style": "none" /* IE and Edge */,
            " scrollbar-width": "none" /* Firefox */,
            "::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {!currentUser && (
            <Box padding={"1.5rem .5rem"}>
              <img src={LOGO} className={classes.logo} alt="logo" />
              <Box padding="1rem 1rem 0 1rem" display="flex" gap={"2rem"}>
                <Typography
                  variant="body1"
                  sx={{ paddingBottom: "5px" }}
                  className={`${isLogin && classes.hoverActive}`}
                >
                  <Link
                    underline="none"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      dispatch({ type: "AUTH_TYPE", payload: true })
                    }
                    color={"textPrimary"}
                    className={`${isLogin && "list-active"}`}
                  >
                    Sign in
                  </Link>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ paddingBottom: "5px" }}
                  className={`${!isLogin && classes.hoverActive}`}
                >
                  <Link
                    underline="none"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      dispatch({ type: "AUTH_TYPE", payload: false })
                    }
                    color={"textPrimary"}
                    className={`${!isLogin && "list-active"}`}
                  >
                    New account
                  </Link>
                </Typography>
              </Box>
              <Divider sx={{ background: grey[200] }} />
              {isLogin && (
                <>
                  <Box padding="2rem 1rem 1rem 1rem">
                    <FormLabel className={classes.label}>Email</FormLabel>
                    <StyledTextField
                      variant="outlined"
                      name={"email"}
                      onChange={(e) =>
                        loginDispatch({
                          type: "EMAIL",
                          payload: e.target.value,
                        })
                      }
                      className={classes.field}
                      value={loginDetails.email}
                      fullWidth
                      placeholder="Enter email"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Email fontSize="small" sx={{ color: grey[300] }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormLabel className={classes.label}>Password</FormLabel>
                    <StyledTextField
                      variant="outlined"
                      name="password"
                      type={"password"}
                      inputProps={{ minLength: 8 }}
                      onChange={(e) =>
                        loginDispatch({
                          type: "PASSWORD",
                          payload: e.target.value,
                        })
                      }
                      className={classes.field}
                      value={loginDetails.password}
                      fullWidth
                      placeholder="Enter password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Lock fontSize="small" sx={{ color: grey[300] }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <LoadingButton
                      loading={loading}
                      variant="contained"
                      disableElevation
                      color="secondary"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        height: "3.5rem",
                        marginBottom: "1rem",
                      }}
                      onClick={() => onSubmit("login")}
                    >
                      Sign in
                    </LoadingButton>
                    <Typography variant="body2" textAlign={"center"}>
                      <Link
                        underline="none"
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          dispatch({ type: "AUTH_CLOSE" });
                          setForgetPass(true);
                        }}
                        color={"secondary"}
                      >
                        Forgot your password?
                      </Link>
                    </Typography>
                  </Box>
                  <Divider sx={{ margin: "1.4rem 0" }} orientation="horizontal">
                    <Typography color="textSecondary" variant="body2">
                      or continue with
                    </Typography>{" "}
                  </Divider>
                  <ButtonGroup fullWidth>
                    <Button
                      className={classes.btnGoogle}
                      sx={{ textTransform: "none" }}
                      onClick={googleAuth}
                      disableElevation
                      variant="outlined"
                      fullWidth
                      color="secondary"
                    >
                      <img
                        style={{ marginRight: "1rem" }}
                        width="12%"
                        src={Google}
                        alt="google_icon"
                      />{" "}
                      Google
                    </Button>
                    <Button
                      className={classes.btnGoogle}
                      sx={{ textTransform: "none" }}
                      onClick={facebookAuth}
                      startIcon={<Facebook fontSize="large" />}
                      disableElevation
                      variant="outlined"
                      fullWidth
                      color="secondary"
                    >
                      Facebook
                    </Button>
                    {/* <AppleLogin
                                    clientId="com.bpohomes.bpohomes"
                                    redirectURI="http://localhost:3001/"
                                    usePopup={true}
                                    callback={appleResponse} // Catch the response
                                    scope="email name"
                                    responseMode="query"
                                    render={ renderProps => (
                                            <Button
                                                className={classes.btnGoogle} sx={{textTransform: 'none'}}
                                                startIcon={<Apple sx={{ color: '#000' }} />}
                                                disableElevation
                                                variant='outlined'
                                                fullWidth
                                                color='secondary'
                                                onClick={renderProps.onClick}
                                            >
                                                Apple
                                            </Button>
                                    )}
                                >

                                </AppleLogin> */}
                  </ButtonGroup>
                </>
              )}
              {!isLogin && (
                <Box
                  padding="2rem 1rem 1rem 1rem"
                  height={"34.2rem"}
                  overflow="scroll"
                >
                  <FormLabel className={classes.label}>First Name</FormLabel>
                  <StyledTextField
                    variant="outlined"
                    className={classes.field}
                    value={signupDetails.firstname}
                    fullWidth
                    placeholder="Enter Firstname"
                    name="first_name"
                    onChange={(e) =>
                      signupDispatch({
                        type: "FIRSTNAME",
                        payload: e.target.value,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Person fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormLabel className={classes.label}>Last Name</FormLabel>
                  <StyledTextField
                    variant="outlined"
                    className={classes.field}
                    value={signupDetails.lastname}
                    fullWidth
                    placeholder="Enter Lastname"
                    name="last_name"
                    onChange={(e) =>
                      signupDispatch({
                        type: "LASTNAME",
                        payload: e.target.value,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Person fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormLabel className={classes.label}>Email</FormLabel>
                  <StyledTextField
                    variant="outlined"
                    type={"email"}
                    name={"email"}
                    className={classes.field}
                    value={signupDetails.email}
                    fullWidth
                    placeholder="Enter email"
                    onChange={(e) =>
                      signupDispatch({ type: "EMAIL", payload: e.target.value })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Email fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormLabel className={classes.label}>Phone</FormLabel>
                  <StyledTextField
                    variant="outlined"
                    name="phone"
                    className={classes.field}
                    value={signupDetails.phone}
                    onChange={(e) =>
                      signupDispatch({ type: "PHONE", payload: e.target.value })
                    }
                    fullWidth
                    placeholder="Phone number"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Call fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormLabel className={classes.label}>State</FormLabel>
                  <Select
                    variant="outlined"
                    name="state"
                    className={classes.field}
                    value={signupDetails.state}
                    onChange={(e) =>
                      signupDispatch({ type: "STATE", payload: e.target.value })
                    }
                    fullWidth
                    displayEmpty
                    sx={{
                      height: "48px",
                      "& .MuiSelect-select": {
                        padding: "12px 14px",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select State
                    </MenuItem>
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormLabel className={classes.label}>Password</FormLabel>
                  <StyledTextField
                    variant="outlined"
                    name="password"
                    type={"password"}
                    className={classes.field}
                    value={signupDetails.password}
                    onChange={(e) =>
                      signupDispatch({
                        type: "PASSWORD",
                        payload: e.target.value,
                      })
                    }
                    inputProps={{ minLength: 8 }}
                    fullWidth
                    placeholder="Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Lock fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormLabel className={classes.label}>
                    Confirm Password
                  </FormLabel>
                  <StyledTextField
                    variant="outlined"
                    type={"password"}
                    className={classes.field}
                    value={signupDetails.confirmPassword}
                    onChange={(e) =>
                      signupDispatch({
                        type: "CONFIRM_PASSWORD",
                        payload: e.target.value,
                      })
                    }
                    name="passwordConfirmation"
                    inputProps={{ minLength: 8 }}
                    fullWidth
                    placeholder="Confirm Password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Lock fontSize="small" sx={{ color: grey[300] }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControlLabel
                    sx={{ marginTop: "-8px", mb: 2 }}
                    control={
                      <Checkbox
                        checked={signupDetails.isAgent}
                        id="prof"
                        name={"isProfessional"}
                        onChange={() =>
                          signupDispatch({
                            type: "AGENT",
                            payload: !signupDetails.isAgent,
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I am a BPO Homes Realty Agent
                      </Typography>
                    }
                  />
                  <FormLabel
                    className={classes.label}
                    sx={{ display: "block", mb: 1 }}
                  >
                    Role
                  </FormLabel>
                  <Select
                    variant="outlined"
                    name="role"
                    className={classes.field}
                    disabled={signupDetails.isAgent}
                    value={signupDetails.role}
                    onChange={(e) =>
                      signupDispatch({
                        type: "ROLE",
                        payload: e.target.value,
                      })
                    }
                    fullWidth
                    displayEmpty
                    sx={{
                      height: "48px",
                      "& .MuiSelect-select": {
                        padding: "12px 14px",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Role
                    </MenuItem>
                    {signupRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <LoadingButton
                    disabled={canNotSubmit}
                    loading={loading}
                    variant="contained"
                    disableElevation
                    color="secondary"
                    fullWidth
                    sx={{
                      textTransform: "none",
                      height: "3.5rem",
                      marginBottom: "1rem",
                    }}
                    onClick={() => onSubmit("signup")}
                  >
                    Sign Up
                  </LoadingButton>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "13px" }}
                    color={"textSecondary"}
                  >
                    By signing in, you agree to BPO Homes's{" "}
                    <Link>Terms of Use</Link> and <Link>Privacy Policy</Link>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Email Verification */}
          {currentUser && !currentUser?.is_email_verified && (
            <Box padding={"2rem 1.5rem"} textAlign={"center"}>
              <img src={EMAIL} width={"22%"} alt="email" />
              <Typography variant="h5" fontWeight={500} mt={1} mb={1}>
                Verify Email
              </Typography>
              <Typography variant="body1" mb={2}>
                To start using bpohomes.com, we have sent a verification token
                to your email (<b>{currentUser?.email}</b>)
              </Typography>
              <Stack
                direction={"row"}
                gap={".6rem"}
                my={3}
                width={"100%"}
                mx={"auto"}
              >
                {new Array(6).fill(0).map((el, i) => {
                  return (
                    <input
                      key={i}
                      autoFocus={i === 0 ? true : false}
                      ref={
                        i === 0
                          ? ref1
                          : i === 1
                            ? ref2
                            : i === 2
                              ? ref3
                              : i === 3
                                ? ref4
                                : i === 4
                                  ? ref5
                                  : ref6
                      }
                      type="text"
                      maxLength={1}
                      onChange={(e) => autoFocusHandler(e)}
                      style={{
                        textAlign: "center",
                        height: "4rem",
                        fontSize: "2rem",
                        width: "100%",
                        borderRadius: "3px",
                        border: "1px solid lightgrey",
                      }}
                    />
                  );
                })}
              </Stack>
              <LoadingButton
                loading={loading}
                variant="contained"
                disableElevation
                color="secondary"
                fullWidth
                size="large"
                sx={{ textTransform: "none", marginBottom: "1rem" }}
                onClick={verifyAccount}
              >
                Verify Account
              </LoadingButton>
              <Typography
                variant="body2"
                onClick={sendOTPCode}
                color={"secondary"}
                fontWeight={400}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Didn't receive email. Resend
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Forget Password */}
      <Dialog open={forgetPass} maxWidth={"xs"}>
        <DialogContent sx={{ position: "relative" }}>
          <IconButton
            sx={{ position: "absolute", top: "3%", right: "2%" }}
            onClick={() => {
              setForgetPass(false);
              dispatch({ type: "AUTH_LOGIN" });
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Box padding={"1.5rem .5rem"}>
            {page === 1 && (
              <Box>
                <Typography textAlign={"center"} variant="h6">
                  Forgot your password?
                </Typography>
                <Typography mb={4} textAlign={"center"} variant="body2">
                  Enter your email address and we'll send you a link to set your
                  password.
                </Typography>

                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter Email"
                  onChange={(e) => setResetEmail(e.target.value)}
                />
                <LoadingButton
                  loading={loading}
                  disableElevation
                  onClick={onPassReset}
                  sx={{
                    textTransform: "none",
                    marginTop: "1rem",
                    fontWeight: 500,
                  }}
                  fullWidth
                  variant="contained"
                  color="secondary"
                >
                  Send Reset Link
                </LoadingButton>
                <Typography
                  mt={1}
                  textAlign={"center"}
                  sx={{ fontSize: ".9rem" }}
                >
                  Know your password?
                  <Button
                    disableElevation
                    color="secondary"
                    onClick={() => {
                      setForgetPass(false);
                      dispatch({ type: "AUTH_LOGIN" });
                    }}
                    sx={{ textTransform: "none" }}
                    variant="text"
                  >
                    Sign in
                  </Button>
                </Typography>
              </Box>
            )}
            {page === 2 && (
              <Box>
                <Typography textAlign={"center"} variant="h6">
                  Your forgot password email was sent!
                </Typography>
                <Typography mb={4} textAlign={"center"} variant="body2">
                  If the email address has an account, an email will be sent
                  with a link to reset your password.
                </Typography>
                <Typography>Email sent to: {resetEmail}</Typography>
                <Divider sx={{ my: "1rem" }} />
                <Typography
                  mt={1}
                  textAlign={"center"}
                  sx={{ fontSize: ".9rem" }}
                >
                  Know your password?
                  <Button
                    disableElevation
                    color="secondary"
                    onClick={() => {
                      setForgetPass(false);
                      dispatch({ type: "AUTH_LOGIN" });
                    }}
                    sx={{ textTransform: "none" }}
                    variant="text"
                  >
                    Sign in
                  </Button>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <SideBar open={openDraw} toggle={toggleDrawer} />
    </>
  );
};

const mapStateToprops = (state) => {
  //console.log(state)
  return state;
};

export default connect(mapStateToprops, {
  userSignUp,
  userLogin,
  signoutUser,
  googleOAuth,
  facebookOAuth,
  googleOneTap,
})(NavBar);
